import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useId,
  type ReactNode,
} from 'react'
import type { CSSVariableStyle } from '@/lib/css-variables'
import { cn } from '@/lib/utils'

type DetailRowsContextValue = {
  headerIds: string[]
}

const DetailRowsContext = createContext<DetailRowsContextValue>({
  headerIds: [],
})

export type DetailRowsProps = {
  /** Column count when there are no `headers`; headers set the count otherwise. */
  columns?: number
  headers?: string[]
  ariaLabel?: string
  className?: string
  children?: ReactNode
}

function DetailRows({
  columns = 2,
  headers = [],
  ariaLabel,
  className,
  children,
}: DetailRowsProps) {
  const reactId = useId()
  const tableId = `detail-rows-${reactId.replaceAll(':', '')}`
  const columnCount = headers.length || columns
  const headerIds = headers.map((_, index) => `${tableId}-column-${index}`)
  const style: CSSVariableStyle = {
    '--detail-row-columns': `repeat(${columnCount}, minmax(0, 1fr))`,
  }

  return (
    <DetailRowsContext.Provider value={{ headerIds }}>
      <div
        data-slot="detail-rows"
        role="table"
        aria-label={ariaLabel}
        className={cn(
          '@container/detail-rows overflow-hidden rounded-lg border border-border/60 bg-card text-sm',
          className
        )}
        style={style}
      >
        {headers.length ? (
          <DetailRow
            header
            className="sr-only bg-muted/40 text-xs font-semibold text-muted-foreground @[34rem]/detail-rows:not-sr-only @[34rem]/detail-rows:grid"
          >
            {headers.map((header, index) => (
              <DetailCell key={`${header}-${index}`} id={headerIds[index]}>
                {header}
              </DetailCell>
            ))}
          </DetailRow>
        ) : null}
        {children}
      </div>
    </DetailRowsContext.Provider>
  )
}

export type DetailRowProps = {
  header?: boolean
  className?: string
  children?: ReactNode
}

function DetailRow({ header = false, className, children }: DetailRowProps) {
  const cells = Children.map(children, (child, index) => {
    if (!isValidElement<DetailCellProps>(child)) {
      return child
    }

    return cloneElement(child, {
      columnIndex: child.props.columnIndex ?? index,
      header: child.props.header ?? header,
    })
  })

  return (
    <div
      data-slot="detail-row"
      role="row"
      className={cn(
        'grid gap-x-3 gap-y-1 border-b border-border/60 px-4 py-3 last:border-b-0 @[34rem]/detail-rows:grid-cols-[var(--detail-row-columns)] @[34rem]/detail-rows:items-center @[34rem]/detail-rows:gap-0 @[34rem]/detail-rows:px-0 @[34rem]/detail-rows:py-0',
        !header && 'transition-colors @[34rem]/detail-rows:hover:bg-muted/20',
        className
      )}
    >
      {cells}
    </div>
  )
}

export type DetailCellProps = {
  /** Shown above the value only in the stacked (narrow) layout. */
  label?: ReactNode
  header?: boolean
  id?: string
  columnIndex?: number
  colSpan?: number
  className?: string
  children?: ReactNode
}

function DetailCell({
  label,
  header = false,
  id,
  columnIndex = 0,
  colSpan,
  className,
  children,
}: DetailCellProps) {
  const reactId = useId()
  const { headerIds } = useContext(DetailRowsContext)
  const cellContentId = `detail-cell-${reactId.replaceAll(':', '')}`
  const headerId = headerIds[columnIndex]
  const labelledBy = headerId && !colSpan ? `${headerId} ${cellContentId}` : undefined

  return (
    <div
      data-slot="detail-cell"
      id={header ? id : undefined}
      role={header ? 'columnheader' : 'cell'}
      aria-colindex={columnIndex + 1}
      aria-colspan={colSpan}
      aria-labelledby={header ? undefined : labelledBy}
      className={cn(
        'min-w-0 break-words @[34rem]/detail-rows:px-4',
        header ? '@[34rem]/detail-rows:py-2.5' : '@[34rem]/detail-rows:py-3',
        className
      )}
    >
      {label ? (
        <div
          aria-hidden="true"
          className="mb-1 text-xs font-medium text-muted-foreground @[34rem]/detail-rows:hidden"
        >
          {label}
        </div>
      ) : null}
      <div id={cellContentId}>{children}</div>
    </div>
  )
}

export { DetailCell, DetailRow, DetailRows }
